import { calculateDistance } from "./locationUtils";
import { extractStoreCoordinates, getStoreById } from "../services/store.service";

/** Must match product rule; backend uses GPS_VALIDATION_RADIUS_METERS (set to 200 in env). */
export const STORE_CHECK_IN_RADIUS_METERS = 200;

function readStoredProfile(): Record<string, unknown> | undefined {
  try {
    const raw = localStorage.getItem("profile");
    if (!raw) return undefined;
    return JSON.parse(raw) as Record<string, unknown>;
  } catch {
    return undefined;
  }
}

function storeIdFromProfile(profile: Record<string, unknown> | undefined): number | null {
  if (!profile) return null;
  const partner = profile.partner as Record<string, unknown> | undefined;
  const candidates = [profile.store_id, partner?.store_id, profile.store];
  for (const c of candidates) {
    const n = Number(c);
    if (Number.isFinite(n) && n > 0) return n;
  }
  const fromLs = localStorage.getItem("storeId");
  if (fromLs) {
    const n = Number(fromLs);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

/** Partner store coordinates from profile payload or store API. */
export async function resolvePartnerStoreCoordinates(): Promise<{
  lat: number;
  lng: number;
} | null> {
  const profile = readStoredProfile();
  const fromProfile = extractStoreCoordinates(profile);
  if (fromProfile) return fromProfile;

  const storeId = storeIdFromProfile(profile);
  if (storeId == null) return null;

  const store = await getStoreById(storeId);
  return extractStoreCoordinates(store);
}

export type StoreCheckInGeofenceResult =
  | { ok: true; distanceMeters?: number }
  | { ok: false; message: string };

/**
 * Client-side check-in geofence. When store coordinates are unknown, returns ok
 * and relies on the server (POST /delivery/attendance/mark/).
 */
export async function validateStoreCheckInGeofence(
  userLat: number,
  userLng: number
): Promise<StoreCheckInGeofenceResult> {
  const store = await resolvePartnerStoreCoordinates();
  if (!store) {
    return { ok: true };
  }

  const distanceMeters = calculateDistance(
    userLat,
    userLng,
    store.lat,
    store.lng
  );

  if (distanceMeters > STORE_CHECK_IN_RADIUS_METERS) {
    return {
      ok: false,
      message: `You must be within ${STORE_CHECK_IN_RADIUS_METERS} m of your store to check in. You are about ${Math.round(distanceMeters)} m away.`,
    };
  }

  return { ok: true, distanceMeters };
}
